import React, { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'
import { IconSend, IconHeadphones, IconHeadphonesOff, IconFileText } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { Button } from '@/components/buttons/Button'
import { toast } from '@/components/toasts/toast.helpers'
import {
  startListeningToAgentAlert,
  stopListeningToAlert,
  resolveLiveCoaching,
  fetchChatHistory,
} from '@/reducers/realtime/realtime.actions'
import { stopListeningToCall } from '@/reducers/commandCenter/commandCenter.redux'
import { toggleTranscript } from '@/reducers/realtime/liveTranscripts.redux'
import { SET_ACTIVE_USERNAME, selectOpenChats } from '@/reducers/realtime/realtimeChats.redux'
import { hasLiveListenAccess } from '@/reducers/auth/currentUser.redux'

// A raised_hand alert carries this sentinel instead of a real processing URL,
// so Listen has nothing to join from it alone.
const RAISED_HAND_SENTINEL = 'raised_hand'

// Sub-pixel scroll positions and rounding mean "at the very top/bottom" is
// never exactly 0, so both edge checks need a little slack.
const SCROLL_EDGE_SLACK_PX = 8

export const ChatPanel = () => {
  const dispatch = useDispatch()
  const {
    chats = {},
    activeUsername = null,
    panelOpen = false,
  } = useSelector((state) => state.realtimeChats) || {}
  const { isOpen: rtcSocketIsOpen } = useSelector((state) => state.realtimeSocket)
  const { socketOpen: ccSocketOpen, agents: commandCenterAgents = [] } = useSelector(
    (state) => state.commandCenter
  )
  const { listeningToAgent } = useSelector((state) => state.commandCenter)
  const { open: transcriptOpen } = useSelector((state) => state.liveTranscripts)
  // Listen lives in the chat: the live alert for the active agent carries the
  // call/processing context, and listeningToUsername says whether we're on it.
  const { listeningToUsername, alerts } = useSelector((state) => state.realtimeUserAlerts)
  const currentUser = useSelector((state) => state.currentUser)
  const currentOrganization = useSelector((state) => state.currentOrganization)
  const flags = useFlags()
  // Chat reaches everyone; the live call content inside it does not. An org
  // that turned Live Listen off must not get the same call through the widget.
  const canSeeLiveCallContent = hasLiveListenAccess(currentOrganization, flags)
  const [chatMessage, setChatMessage] = useState('')

  const messageListRef = useRef(null)
  // Pure DOM measurements (not React state) — used to restore scroll position
  // after older messages are prepended and to decide whether to auto-follow
  // tail messages when the user is pinned to the bottom.
  const prevScrollHeightRef = useRef(0)
  const wasAtBottomRef = useRef(true)
  // Cursor of the last older-history page we asked for. A page that comes back
  // holding only messages we already have leaves scrollTop at the top, which
  // re-triggers the scroll handler — without this the same cursor would be
  // refetched forever.
  const lastHistoryCursorRef = useRef(null)

  // Every open chat, in frozen raise order. The list never reshuffles on new
  // messages. The Agent Assist panel is the only chat surface, so nothing is
  // filtered out here.
  const visibleChats = selectOpenChats(chats).sort(
    (first, second) => first.raisedSeq - second.raisedSeq
  )

  // Show the active chat; if activeUsername points at nothing, fall back to the
  // first open chat.
  const activeChat = chats[activeUsername] || null
  const effectiveActiveUsername = activeChat
    ? activeUsername
    : visibleChats[0]?.agentUsername || null
  const activeUnread = chats[effectiveActiveUsername]?.unread || 0

  // While the panel is open, keep redux's activeUsername in sync with the thread
  // actually shown and clear its unread badge (covers the case where the panel
  // opens on an already-active agent that accrued unread while closed).
  useEffect(() => {
    if (
      panelOpen &&
      effectiveActiveUsername &&
      (effectiveActiveUsername !== activeUsername || activeUnread > 0)
    ) {
      dispatch({ type: SET_ACTIVE_USERNAME, activeUsername: effectiveActiveUsername })
    }
  }, [panelOpen, effectiveActiveUsername, activeUsername, activeUnread, dispatch])

  const active = chats[effectiveActiveUsername] || {}
  const {
    messages: activeMessages = [],
    alertId: activeAlertId,
    callId: activeCallId,
    agentId: activeAgentId,
    managerId: activeManagerId,
    agentUsername: activeAgentUsername,
    agentLastName: activeAgentLastName,
    agentFirstName: activeAgentFirstName,
    managerUsername: activeManagerUsername,
    historyLoading = false,
    hasMore = true,
  } = active

  // The agent's live RTC alert carries the call_id/processing_url Listen needs.
  // A real (non-sentinel) processing URL already in memory lets us join
  // immediately; a raised_hand alert only carries the sentinel, and a
  // cold-joined chat has no in-memory alert at all — in both those cases we
  // resolve the live processing URL at click time from the persisted call_id.
  const liveAlert = (activeAgentUsername && alerts?.[activeAgentUsername]) || null
  const alertProcessingUrl =
    liveAlert?.processingUrl && liveAlert.processingUrl !== RAISED_HAND_SENTINEL
      ? liveAlert.processingUrl
      : null
  // Managers without RTC access have no alerts at all — Live Listen's agent
  // roster is their only source of "is this agent on a call, and where". It
  // only counts while the CC socket is feeding it: nothing clears the roster
  // when that page unmounts, so a stale row would keep offering Listen on a
  // call that ended hours ago.
  const commandCenterAgent = ccSocketOpen
    ? commandCenterAgents.find((agent) => agent.agent_username === activeAgentUsername) || null
    : null
  const warmProcessingUrl = alertProcessingUrl || commandCenterAgent?.call_data?.call_processing_url

  const isOnCall = Boolean(liveAlert) || Boolean(commandCenterAgent?.call_data?.call_processing_url)
  // The thread outlives the call, so its stored callId goes stale. Prefer
  // whichever source knows about the call happening right now.
  // Only trust the roster's call id while that call is actually running — a row
  // keeps its `call_data` after the call ends, and tagging a chat with a
  // finished call is worse than falling back to the thread's own id.
  const rosterCallId = commandCenterAgent?.call_data?.call_processing_url
    ? commandCenterAgent.call_data.call_id
    : null
  const liveCallId = liveAlert?.callId || rosterCallId || activeCallId
  const isListeningToActive =
    Boolean(activeAgentUsername) &&
    (listeningToUsername === activeAgentUsername || listeningToAgent === activeAgentUsername)
  // Offer Listen only while the agent is actually on a call (their live
  // alert record exists — CALL_END tears it down; a lowered hand does NOT,
  // so managers can listen for the whole call). The chat itself outlives the
  // call, and its persisted callId would otherwise keep offering a Listen
  // that can only toast "no longer live". Also needs a joinable target: a
  // warm URL or a call_id the click-time resolver can use.
  const canListen =
    canSeeLiveCallContent &&
    !isListeningToActive &&
    isOnCall &&
    Boolean(warmProcessingUrl || liveCallId)

  const scrollToBottom = () => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight
    }
  }

  // A thread opens on a clean slate. "End chat" means the supervisor is done
  // with that agent, so reopening must not silently resurrect the conversation
  // they just closed — the backlog is fetched only when they ask for it.
  useEffect(() => {
    wasAtBottomRef.current = true
    prevScrollHeightRef.current = 0
    lastHistoryCursorRef.current = null
  }, [activeAgentUsername])

  // Handles two cases driven by the messages array changing length:
  //   1. Older messages just prepended (prevScrollHeightRef was captured before
  //      the paginate dispatch) — restore by shifting scrollTop by the height
  //      delta so the supervisor's anchor row stays visually fixed.
  //   2. Live message tail — auto-follow only if they're at the bottom.
  useLayoutEffect(() => {
    const container = messageListRef.current
    if (!container) {
      return
    }

    if (prevScrollHeightRef.current > 0) {
      container.scrollTop = container.scrollHeight - prevScrollHeightRef.current
      prevScrollHeightRef.current = 0
      return
    }

    if (wasAtBottomRef.current) {
      container.scrollTop = container.scrollHeight
    }
    // Keyed on the array itself, not its length: a paginate fetch that returns
    // only messages we already have still hands back a new array, and this
    // effect is what disarms prevScrollHeightRef. Keyed on length it would stay
    // armed and jump the next live message to the wrong offset.
  }, [activeMessages])

  const loadEarlierMessages = async () => {
    if (!activeAgentUsername || historyLoading || !hasMore) {
      return
    }

    // Page back from the oldest message on screen, or from now when the thread
    // is empty — a reopened chat has nothing to anchor to.
    const oldestPersisted = activeMessages.find((messageItem) => messageItem.createdAtIso)
    const before = oldestPersisted ? oldestPersisted.createdAtIso : new Date().toISOString()
    if (lastHistoryCursorRef.current === before) {
      return
    }

    lastHistoryCursorRef.current = before
    if (messageListRef.current) {
      prevScrollHeightRef.current = messageListRef.current.scrollHeight
    }
    const loaded = await dispatch(fetchChatHistory({ agentUsername: activeAgentUsername, before }))
    if (!loaded) {
      // Without this the guard above pins this cursor forever and scrolling up
      // again is a no-op, so a single failed page ends pagination for the thread.
      lastHistoryCursorRef.current = null
    }
  }

  const handleMessagesScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    wasAtBottomRef.current = scrollHeight - scrollTop - clientHeight < SCROLL_EDGE_SLACK_PX

    if (scrollTop < SCROLL_EDGE_SLACK_PX) {
      loadEarlierMessages()
    }
  }

  const startListening = async () => {
    let processingUrl = warmProcessingUrl
    // Cold path / raised_hand: no usable in-memory URL, so resolve the current
    // one from the persisted call_id (and confirm the call is still live).
    if (!processingUrl) {
      const resolved = await dispatch(
        resolveLiveCoaching({
          callId: liveCallId,
          agentUsername: activeAgentUsername,
        })
      )
      if (!resolved?.isLive || !resolved.processingUrl) {
        toast.error('That call is no longer live')
        return
      }
      processingUrl = resolved.processingUrl
    }
    dispatch(
      startListeningToAgentAlert({
        agentUsername: activeAgentUsername,
        alertCallId: liveCallId,
        processingUrl,
        managerUsername: currentUser.username,
        alertId: liveAlert?.alertId || activeAlertId || RAISED_HAND_SENTINEL,
      })
    )
  }

  const stopListening = () => {
    dispatch(stopListeningToAlert())
    dispatch(stopListeningToCall())
  }

  const sendMessage = () => {
    const message = chatMessage
    if (isEmpty(message)) {
      return
    }

    dispatch({
      type: 'realtime/addMessageToChat',
      agentUsername: activeAgentUsername,
      message,
      isMessageFromManager: true,
      date: moment().format('LT'),
    })

    const chatPayload = {
      message,
      alert_id: activeAlertId,
      call_id: liveCallId || null,
      to_user_id: activeAgentId,
      from_user_id: activeManagerId,
      agent_username: activeAgentUsername,
      manager_username: activeManagerUsername,
    }

    // Prefer the RTC socket (legacy path) when it's open; fall back to the CC
    // socket so managers without RTC access can still send chats.
    if (rtcSocketIsOpen) {
      dispatch({ type: 'SEND_CHAT_TO_DESKTOP', ...chatPayload })
    } else if (ccSocketOpen) {
      dispatch({ type: 'commandCenter/sendChat', ...chatPayload })
    }

    setChatMessage('')
    scrollToBottom()
  }

  if (!activeAgentUsername) {
    return (
      <div className="assist-empty" data-testid="assist-empty">
        No agents need help right now.
      </div>
    )
  }

  return (
    <>
      <div className="chat-agent-name">
        <div className="chat-agent-name__identity">
          <span className="chat-avatar">
            {(activeAgentFirstName || '')[0]}
            {(activeAgentLastName || '')[0]}
          </span>
          <div className="chat-agent-name__meta">
            <span>{`${activeAgentFirstName} ${activeAgentLastName}`}</span>
            {isOnCall && <span className="chat-agent-name__status">On call</span>}
          </div>
        </div>
        <div className="chat-agent-name__actions">
          {isOnCall && canSeeLiveCallContent && (
            <Button
              square
              small
              className={classNames('chat-transcript-toggle', {
                'chat-transcript-toggle--active': transcriptOpen,
              })}
              data-testid="chat-transcript-toggle"
              tooltip={transcriptOpen ? 'Hide transcript' : 'Show transcript'}
              onClick={() => dispatch(toggleTranscript())}
            >
              <IconFileText />
            </Button>
          )}
          {isListeningToActive ? (
            <Button small destructive onClick={stopListening} data-testid="chat-stop-listen">
              <IconHeadphonesOff />
              Stop
            </Button>
          ) : (
            canListen && (
              <Button small onClick={startListening} data-testid="chat-listen">
                <IconHeadphones />
                Listen
              </Button>
            )
          )}
        </div>
      </div>
      <div className="chat-body">
        <div className="chat-area" ref={messageListRef} onScroll={handleMessagesScroll}>
          {hasMore && (
            <Button
              small
              basic
              className="chat-load-earlier"
              data-testid="chat-load-earlier"
              loading={historyLoading}
              onClick={loadEarlierMessages}
            >
              Load earlier messages
            </Button>
          )}
          {activeMessages.map((messageItem, index) => {
            // Group consecutive messages from the same sender (within the
            // same minute) under one timestamp — only the first of a run
            // shows the time; the rest tuck in below it.
            const previous = activeMessages[index - 1]
            const startsRun =
              !previous ||
              previous.isMessageFromManager !== messageItem.isMessageFromManager ||
              previous.date !== messageItem.date
            return (
              <div
                key={messageItem.id || messageItem.localId || `unsaved-${index}`}
                className={classNames('chat-message', {
                  'chat-message--manager': messageItem.isMessageFromManager,
                  'chat-message--agent': !messageItem.isMessageFromManager,
                })}
              >
                {startsRun && (
                  <div
                    className={
                      messageItem.isMessageFromManager ? 'chat-time-manager' : 'chat-time-agent'
                    }
                  >
                    {messageItem.date}
                  </div>
                )}
                <div
                  className={classNames({
                    'chat-text-manager': messageItem.isMessageFromManager,
                    'chat-text-agent': !messageItem.isMessageFromManager,
                  })}
                >
                  {messageItem.message}
                </div>
              </div>
            )
          })}
        </div>
        <div className="chat-footer">
          <div className="chat-text-input-container">
            <textarea
              data-testid="chat-box"
              className="chat-textarea"
              value={chatMessage}
              autoFocus
              placeholder="Message agent…"
              onChange={(event) => setChatMessage(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  sendMessage()
                }
              }}
            />
            <Button
              square
              small
              brand
              className="chat-icon"
              data-testid="send-chat"
              onClick={sendMessage}
            >
              <IconSend />
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
